import React, { useMemo } from 'react';
import { Card, Row, Col, Tag } from 'antd';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { formatWorkPriceDisplay } from '../../../utils/workPriceUtils';
import { toJalali } from '../../../utils/dateUtils';
import { formatReportNumberFa, REPORT_CHART_COLORS, REPORT_CHART_PALETTE } from '../../../pages/reports/chartFormatters';
import { CountBarTooltip, AmountBarTooltip } from '../../../pages/reports/ReportChartTooltips';
import ChartEmptyState from '../../../pages/reports/ChartEmptyState';

const font = { fontFamily: 'CustomFont' };
const label = (t) => <span style={font}>{t}</span>;

const chartBoxClass =
  'h-64 sm:h-72 border border-slate-200/80 dark:border-slate-600 rounded-xl p-3 bg-white dark:bg-slate-900/50';

const KpiCard = ({ title, value, accent = 'text-brand-primary' }) => (
  <Card
    size="small"
    className="shadow-sm border border-slate-100 dark:border-slate-700 h-full rounded-xl"
    bodyStyle={{ padding: '16px' }}
  >
    <div className="text-slate-500 text-xs mb-2" style={font}>
      {title}
    </div>
    <div className={`text-lg sm:text-xl font-bold truncate tabular-nums ${accent}`} style={font}>
      {value}
    </div>
  </Card>
);

const formatProjectDate = (d) => {
  if (!d) return '—';
  return toJalali(d) || d;
};

const TaskPieChart = ({ chart }) => {
  const data = useMemo(() => {
    if (!chart?.labels?.length) return [];
    return chart.labels.map((lbl, i) => ({
      label: lbl,
      value: Number(chart.values?.[i] || 0),
      color: chart.colors?.[i] || REPORT_CHART_PALETTE[i % REPORT_CHART_PALETTE.length],
    }));
  }, [chart]);

  const hasData = data.some((d) => d.value > 0);

  return (
    <div className={chartBoxClass} dir="ltr">
      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 custom-font" dir="rtl">
        توزیع وظایف
      </p>
      {hasData ? (
        <ResponsiveContainer width="100%" height="88%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="label" innerRadius={52} outerRadius={88} paddingAngle={2}>
              {data.map((entry) => (
                <Cell key={entry.label} fill={entry.color} stroke="transparent" />
              ))}
            </Pie>
            <Tooltip content={<CountBarTooltip />} />
            <Legend wrapperStyle={{ direction: 'rtl', fontFamily: 'CustomFont', fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <ChartEmptyState minHeight={220} />
      )}
    </div>
  );
};

const BudgetBarChart = ({ chart, title, isAmount }) => {
  const barData = useMemo(() => {
    if (!chart?.labels?.length || !chart?.datasets?.length) return [];
    return chart.labels.map((lbl, i) => ({
      label: lbl,
      planned: Number(chart.datasets[0]?.values?.[i] ?? 0),
      extra: Number(chart.datasets[1]?.values?.[i] ?? 0),
    }));
  }, [chart]);

  const threshold = Number(chart?.threshold || 0);
  const hasData = barData.some((d) => d.planned > 0 || d.extra > 0);

  return (
    <div className={chartBoxClass} dir="ltr">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2" dir="rtl">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 m-0 custom-font">{title}</p>
        <span className="text-xs text-slate-500 custom-font">
          سقف: {isAmount ? formatWorkPriceDisplay(threshold) : formatReportNumberFa(threshold)}
        </span>
      </div>
      {hasData ? (
        <ResponsiveContainer width="100%" height="82%">
          <BarChart data={barData} margin={{ top: 6, right: 8, left: 0, bottom: 4 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fontFamily: 'CustomFont' }}
              tickMargin={6}
              interval={0}
              angle={-20}
              textAnchor="end"
              height={48}
            />
            <YAxis
              width={isAmount ? 72 : 48}
              tick={{ fontSize: 10 }}
              tickFormatter={(v) => (isAmount ? formatReportNumberFa(v) : formatReportNumberFa(v))}
            />
            <Tooltip content={isAmount ? <AmountBarTooltip /> : <CountBarTooltip />} />
            <Legend wrapperStyle={{ direction: 'rtl', fontFamily: 'CustomFont', fontSize: 11 }} />
            <Bar dataKey="planned" name="برنامه‌ریزی" fill={REPORT_CHART_COLORS.teal} radius={[4, 4, 0, 0]} />
            <Bar dataKey="extra" name="اضافه" fill={REPORT_CHART_COLORS.sky} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <ChartEmptyState minHeight={200} />
      )}
    </div>
  );
};

const ProjectOverviewTab = ({ overview, project }) => {
  if (!overview && !project) {
    return (
      <p className="text-gray-500" style={font}>
        داده‌ای برای نمایش نیست
      </p>
    );
  }

  const ov = overview || {};

  return (
    <div style={font} className="space-y-4">
      <Row gutter={[12, 12]}>
        <Col xs={12} sm={6}>
          <KpiCard title={label('ساعات ثبت‌شده')} value={ov.hours_logged || '—'} />
        </Col>
        <Col xs={12} sm={6}>
          <KpiCard
            title={label('درآمد')}
            value={formatWorkPriceDisplay(ov.earnings)}
            accent="text-emerald-600"
          />
        </Col>
        <Col xs={12} sm={6}>
          <KpiCard
            title={label('هزینه')}
            value={formatWorkPriceDisplay(ov.expenses)}
            accent="text-amber-600"
          />
        </Col>
        <Col xs={12} sm={6}>
          <KpiCard
            title={label('سود')}
            value={formatWorkPriceDisplay(ov.profit)}
            accent="text-sky-600"
          />
        </Col>
      </Row>

      {ov.task_board_status?.length ? (
        <Card size="small" className="shadow-sm border border-slate-100 dark:border-slate-700 rounded-xl" title={label('وضعیت‌های برد وظیفه')}>
          <div className="flex flex-wrap gap-2">
            {ov.task_board_status.map((s) => (
              <Tag key={s.id} color={s.label_color} style={font}>
                {s.column_name}
              </Tag>
            ))}
          </div>
        </Card>
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <TaskPieChart chart={ov.task_chart} />
        </Col>
        <Col xs={24} lg={12}>
          <BudgetBarChart chart={ov.hours_budget_chart} title="بودجه ساعات" />
        </Col>
        <Col xs={24}>
          <BudgetBarChart chart={ov.amount_budget_chart} title="بودجه مبلغ" isAmount />
        </Col>
      </Row>

      {project ? (
        <Card size="small" className="shadow-sm border border-slate-100 dark:border-slate-700 rounded-xl" title={label('اطلاعات پروژه')}>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12}>
              <span className="text-gray-500">{label('مشتری')}: </span>
              {project.client?.name || '—'}
            </Col>
            <Col xs={24} sm={12}>
              <span className="text-gray-500">{label('دسته')}: </span>
              {project.category?.category_name || '—'}
            </Col>
            <Col xs={24} sm={12}>
              <span className="text-gray-500">{label('شروع')}: </span>
              {formatProjectDate(project.start_date)}
            </Col>
            <Col xs={24} sm={12}>
              <span className="text-gray-500">{label('ددلاین')}: </span>
              {formatProjectDate(project.deadline)}
            </Col>
            {project.project_summary ? (
              <Col xs={24}>
                <span className="text-gray-500">{label('خلاصه')}: </span>
                <span className="whitespace-pre-wrap">{project.project_summary}</span>
              </Col>
            ) : null}
          </Row>
        </Card>
      ) : null}
    </div>
  );
};

export default ProjectOverviewTab;
